import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { WorkTypeIcon } from './KanbanBoard';
import { InlineField, RichContent, isHtml, getEffectiveLayout, LABEL_CLS } from './FieldUtils';
import TagsField from './TagsField';
import TimeBlock from './TimeBlock';
import MentionTextarea, { MentionContent } from './MentionTextarea';
import RichEditor, { RichEditorContent } from './RichEditor';
import SubtasksSection from './SubtasksSection';
import AttachmentsSection from './AttachmentsSection';
import LinkedIssuesSection from './LinkedIssuesSection';
import WatchButton from './WatchButton';
import HistoryEntry from './HistoryEntry';
import { useImagePaste } from '../hooks/useImagePaste';
import PastedImagePreview, { removePastedImageMarkdown } from './PastedImagePreview';
import CommentAttachments from './CommentAttachments';
import CommentReactions from './CommentReactions';
import DescriptionAttachments from './DescriptionAttachments';
import CopyTaskLink from './CopyTaskLink';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { useConfirm } from './ConfirmDialog';
import { PriorityPill } from './PriorityIcon';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { getTagColor } from '../lib/tagColor';
import { getDueDateMeta } from '../lib/dueDateColor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Trash, Clock, Flag, User, Tag, ArrowsOut, Paperclip, PencilSimple, FloppyDisk, X as XIcon,
  Link as LinkIcon, Check,
  ChatCircle, ClockCounterClockwise, PaperPlaneRight, ArrowRight,
} from '@phosphor-icons/react';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' }, { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' }, { value: 'done', label: 'Done' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' },
];
const STATUS_COLORS = {
  todo: 'bg-zinc-100 text-zinc-700 border border-zinc-200',
  in_progress: 'bg-blue-50 text-blue-700 border border-blue-200',
  review: 'bg-amber-50 text-amber-700 border border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};
const STATUS_DOT = { todo: 'bg-zinc-400', in_progress: 'bg-blue-500', review: 'bg-amber-500', done: 'bg-emerald-500' };
const PRIORITY_DOT = { low: 'bg-zinc-400', medium: 'bg-amber-500', high: 'bg-orange-500', critical: 'bg-red-500' };
// Enriched dropdown rows: colored dot / avatar beside the label.
const dotNode = (cls, label) => (
  <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full shrink-0 ${cls || 'bg-violet-500'}`} />{label}</span>
);
const assigneeNode = (m) => (
  <span className="flex items-center gap-2 min-w-0">
    <Avatar className="h-5 w-5 flex-shrink-0"><AvatarImage src={m.avatar} /><AvatarFallback className={`${getAvatarStyle(m.user_id || m.name).bg} text-white text-[9px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
    <span className="truncate">{m.name}</span>
  </span>
);
const WT_LABELS = { task: 'Task', story: 'Story', bug: 'Bug' };
const STATUS_LABELS = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
const FIELD_LABELS_MAP = { status: 'Status', priority: 'Priority', assignee: 'Assignee', title: 'Title', work_type: 'Type' };

// Sidebar row: label-left / value-right. Used for the right-side details
// panel so users can scan fields top-down without each label eating its own
// vertical line. Hover gives a subtle highlight so it's obvious every row
// is clickable. The label column is fixed-width so values align.
const SidebarRow = ({ label, children, testid }) => (
  <div
    className="flex items-start gap-3 py-1.5 -mx-2 px-2 rounded-md transition-colors hover:bg-white/70"
    data-testid={testid || `sidebar-row-${String(label).toLowerCase().replace(/\s+/g, '-')}`}
  >
    <span className="w-[105px] flex-shrink-0 pt-[5px] text-[10.5px] uppercase tracking-[0.10em] text-zinc-500 font-bold">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

function BuiltinFieldRenderer({ fieldId, task, members, canEdit, updateField, onPaste, onFileUpload, sprints, uploadImage, layout = 'main', customStatuses }) {
  const assignee = members?.find(m => m.user_id === task?.assignee_id);
  const reporter = members?.find(m => m.user_id === task?.reporter_id);
  const inSidebar = layout === 'sidebar';
  // Built-ins + project custom statuses (board columns added via "Create
  // column"). Without this, tasks already sitting in a custom column can't
  // be moved or edited from the detail sheet because the dropdown lists
  // only the 4 hardcoded states.
  const statusOptions = [
    ...STATUS_OPTIONS.map(s => ({ ...s, node: dotNode(STATUS_DOT[s.value], s.label) })),
    ...(customStatuses || []).map(s => ({ value: s.id, label: s.label, node: dotNode('bg-violet-500', s.label) })),
  ];
  const priorityOptions = PRIORITY_OPTIONS.map(p => ({ ...p, node: dotNode(PRIORITY_DOT[p.value], p.label) }));
  const assigneeOptions = [
    { value: 'unassigned', label: 'Unassigned', node: <span className="flex items-center gap-2 text-zinc-500"><User size={13} /> Unassigned</span> },
    ...(members || []).map(m => ({ value: m.user_id, label: m.name, node: assigneeNode(m) })),
  ];
  const currentStatusLabel = statusOptions.find(s => s.value === task?.status)?.label || task?.status;

  switch (fieldId) {
    case 'description':
      return (
        <div>
          <Label className={LABEL_CLS}>Description</Label>
          <InlineField value={task?.description || ''} type="textarea" richText canEdit={canEdit}
            uploadImage={uploadImage}
            displayValue={<RichContent content={task?.description} />}
            onSave={(v) => updateField('description', v)} onPaste={onPaste} onFileUpload={onFileUpload} />
          <DescriptionAttachments html={task?.description} />
        </div>
      );
    case 'type': {
      const node = (
        <div className="text-[13px] text-zinc-700 flex items-center gap-1.5" data-testid="task-type-readonly">
          <WorkTypeIcon type={task?.work_type || 'task'} size={15} /> {WT_LABELS[task?.work_type] || 'Task'}
        </div>
      );
      return inSidebar ? <SidebarRow label="Type">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Type</Label>{node}</div>
      );
    }
    case 'status': {
      const node = (
        <InlineField value={task?.status} canEdit={canEdit} options={statusOptions}
          displayValue={<Badge className={`${STATUS_COLORS[task?.status] || 'bg-zinc-100 text-zinc-700'} text-[11px]`}>{currentStatusLabel}</Badge>}
          onSave={(v) => updateField('status', v)} />
      );
      return inSidebar ? <SidebarRow label="Status">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Status</Label>{node}</div>
      );
    }
    case 'priority': {
      const node = (
        <InlineField value={task?.priority} canEdit={canEdit} options={priorityOptions}
          displayValue={task?.priority ? <PriorityPill priority={task.priority} /> : <span className="text-zinc-400 text-[13px]">None</span>}
          onSave={(v) => updateField('priority', v)} />
      );
      return inSidebar ? <SidebarRow label="Priority">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Priority</Label>{node}</div>
      );
    }
    case 'assignee': {
      const node = (
        <InlineField value={task?.assignee_id || 'unassigned'} canEdit={canEdit}
          options={assigneeOptions}
          displayValue={assignee ? <span className="flex items-center gap-2 min-w-0"><Avatar className="h-5 w-5 flex-shrink-0"><AvatarImage src={assignee.avatar} /><AvatarFallback className={`${getAvatarStyle(assignee.user_id || assignee.name).bg} text-white text-[9px] font-bold`}>{getInitials(assignee.name)}</AvatarFallback></Avatar><span className="text-[13px] font-medium text-zinc-800 truncate">{assignee.name}</span></span> : <span className="text-zinc-400 flex items-center gap-1 text-[13px]"><User size={13} />Unassigned</span>}
          onSave={(v) => updateField('assignee_id', v === 'unassigned' ? null : v)} />
      );
      return inSidebar ? <SidebarRow label="Assignee">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Assignee</Label>{node}</div>
      );
    }
    case 'due_date': {
      const meta = task?.due_date && task.status !== 'done' ? getDueDateMeta(task.due_date) : null;
      const display = task?.due_date ? (
        <span
          className={`inline-flex items-center gap-1.5 px-2 h-[24px] rounded-md text-[12px] ${meta ? meta.className : 'bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200'}`}
          title={meta?.bucket === 'overdue' ? `Overdue · ${meta.label}` : new Date(task.due_date).toLocaleDateString()}
        >
          <Clock size={12} />
          {meta ? meta.label : new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      ) : <span className="text-zinc-400 text-[13px]">Not set</span>;
      const node = (
        <InlineField value={task?.due_date || ''} type="date" canEdit={canEdit}
          displayValue={display}
          onSave={(v) => updateField('due_date', v || null)} />
      );
      return inSidebar ? <SidebarRow label="Due Date">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Due Date</Label>{node}</div>
      );
    }
    case 'tags': {
      const node = (
        <TagsField projectId={task?.project_id} value={task?.tags || []} canEdit={canEdit}
          onSave={(v) => updateField('tags', v)} />
      );
      return inSidebar ? <SidebarRow label="Tags">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Tags</Label>{node}</div>
      );
    }
    case 'reporter': {
      const node = (
        <div className="flex items-center gap-2 text-[13px] text-zinc-800 min-w-0">
          {reporter ? <><Avatar className="h-5 w-5 flex-shrink-0"><AvatarImage src={reporter.avatar} /><AvatarFallback className={`${getAvatarStyle(reporter.user_id || reporter.name).bg} text-white text-[9px] font-bold`}>{getInitials(reporter.name)}</AvatarFallback></Avatar><span className="font-medium truncate">{reporter.name}</span></> : <span className="text-zinc-400">Unknown</span>}
        </div>
      );
      return inSidebar ? <SidebarRow label="Reporter">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Reporter</Label>{node}</div>
      );
    }
    case 'created': {
      const node = <span className="text-[12px] text-zinc-600">{task?.created_at ? new Date(task.created_at).toLocaleString() : '-'}</span>;
      return inSidebar ? <SidebarRow label="Created">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Created</Label>{node}</div>
      );
    }
    case 'sprint': {
      const currentSprint = sprints?.find(s => s.sprint_id === task?.sprint_id);
      const openSprints = (sprints || []).filter(s => s.status !== 'completed');
      const options = [{ value: 'backlog', label: 'Backlog' }, ...openSprints.map(s => ({ value: s.sprint_id, label: s.name + (s.status === 'active' ? ' · Active' : '') }))];
      // If the task is currently on a completed sprint, include it as a read-only option so the dropdown can display it.
      if (currentSprint && currentSprint.status === 'completed') {
        options.push({ value: currentSprint.sprint_id, label: currentSprint.name + ' · Completed' });
      }
      const node = (
        <InlineField
          value={task?.sprint_id || 'backlog'}
          canEdit={canEdit}
          options={options}
          displayValue={currentSprint
            ? <span className="flex items-center gap-1.5 text-[13px] text-zinc-800 font-medium min-w-0"><span className="truncate">{currentSprint.name}</span>{currentSprint.status === 'active' && <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 border uppercase flex-shrink-0">Active</Badge>}{currentSprint.status === 'completed' && <Badge className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 border uppercase flex-shrink-0">Completed</Badge>}</span>
            : <span className="text-zinc-400 text-[13px]">Backlog</span>}
          onSave={(v) => updateField('sprint_id', v === 'backlog' ? null : v)}
        />
      );
      return inSidebar ? <SidebarRow label="Sprint">{node}</SidebarRow> : (
        <div><Label className={LABEL_CLS}>Sprint</Label>{node}</div>
      );
    }
    default: return null;
  }
}

function CustomFieldRenderer({ field, task, canEdit, updateCustomField, onPaste, onFileUpload, uploadImage, layout = 'main' }) {
  const cfVal = task?.custom_fields?.[field.field_id] || '';
  const opts = field.type === 'select' ? field.options.map(o => ({ value: o, label: o })) : null;
  const isLongText = field.type === 'text' || field.type === 'richtext';
  const inSidebar = layout === 'sidebar';
  // Long-form text fields stay full-width regardless — they wouldn't fit in a
  // 100px label / value-right layout. Short fields (select/number/date/url)
  // get the same SidebarRow alignment as the built-ins.
  if (isLongText) {
    return (
      <div>
        <Label className={LABEL_CLS}>{field.name}</Label>
        <InlineField value={cfVal} type="textarea" richText canEdit={canEdit}
          uploadImage={uploadImage}
          displayValue={<RichContent content={cfVal} />}
          onSave={(v) => updateCustomField(field.field_id, v)} onPaste={onPaste} onFileUpload={onFileUpload} />
      </div>
    );
  }
  const node = field.type === 'url' ? (
    <InlineField value={cfVal} type="url" canEdit={canEdit}
      displayValue={cfVal
        ? <a href={cfVal} target="_blank" rel="noreferrer" className="text-[13px] text-blue-600 hover:text-blue-700 underline underline-offset-2 truncate block max-w-full">{cfVal}</a>
        : <span className="text-zinc-400 text-[13px]">—</span>}
      onSave={(v) => updateCustomField(field.field_id, v)} />
  ) : (
    <InlineField value={cfVal}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      options={opts} canEdit={canEdit}
      onSave={(v) => updateCustomField(field.field_id, v)} />
  );
  return inSidebar ? <SidebarRow label={field.name}>{node}</SidebarRow> : (
    <div><Label className={LABEL_CLS}>{field.name}</Label>{node}</div>
  );
}

export default function TaskSheet({ task, open, onClose, onUpdated, members, canEdit, customFields, fieldLayouts, project, allTasks, onTaskClick }) {
  const { getOrgHeaders, API, user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { handlePaste, handleFileUpload, uploadImage } = useImagePaste(getOrgHeaders);
  const [activeTab, setActiveTab] = useState('comments');
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [directory, setDirectory] = useState([]);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  // Directory = every user who has ever been in this org (incl. deactivated),
  // used to resolve historical comment/history author names & avatars so a
  // removed user never shows as "Unknown".
  useEffect(() => { let on = true; axios.get(`${API}/members/directory`, h()).then(r => { if (on) setDirectory(r.data || []); }).catch(() => {}); return () => { on = false; }; }, [API, h]);
  const resolveUser = (uid) => (members || []).find(m => m.user_id === uid) || directory.find(m => m.user_id === uid);

  useEffect(() => { if (task) { setActiveTab('comments'); setEditingCommentId(null); fetchComments(); fetchActivities(); fetchProjectMembers(); fetchSprints(); } }, [task]);
  const fetchComments = async () => { if (!task) return; try { const { data } = await axios.get(`${API}/tasks/${task.task_id}/comments`, h()); setComments([...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); } catch {} };
  const fetchActivities = async () => { if (!task) return; try { const { data } = await axios.get(`${API}/tasks/${task.task_id}/activities`, h()); setActivities(data); } catch {} };
  const fetchProjectMembers = async () => { if (!task?.project_id) return; try { const { data } = await axios.get(`${API}/projects/${task.project_id}/members`, h()); setProjectMembers(data); } catch {} };
  const fetchSprints = async () => {
    if (!task?.project_id) { setSprints([]); return; }
    try { const { data } = await axios.get(`${API}/projects/${task.project_id}/sprints`, h()); setSprints(data?.sprints || []); } catch {}
  };

  // When a status change targets "done" we surface a soft warning if either
  // (a) any `is_blocked_by` link points at a task that's still open, or
  // (b) any subtask is still not done. Same UX as the Kanban drag-drop guard
  // so the rule is consistent regardless of where the status change is
  // initiated.
  const confirmBlockersIfMovingToDone = async (field, value) => {
    if (field !== 'status' || value !== 'done') return true;
    try {
      const [linksRes, subsRes] = await Promise.allSettled([
        axios.get(`${API}/tasks/${task.task_id}/links`, h()),
        axios.get(`${API}/tasks/${task.task_id}/subtasks`, h()),
      ]);
      const links = linksRes.status === 'fulfilled' ? (linksRes.value.data || []) : [];
      const subs = subsRes.status === 'fulfilled' ? (subsRes.value.data || []) : [];
      const openBlockers = links.filter(l => l.link_type === 'is_blocked_by'
        && l.target?.status && l.target.status !== 'done' && !l.target.is_deleted);
      const openSubs = subs.filter(s => s.status !== 'done' && !s.is_deleted);
      if (openBlockers.length === 0 && openSubs.length === 0) return true;
      const lines = [];
      if (openSubs.length > 0) {
        const list = openSubs.slice(0, 3).map(s => s.task_key).join(', ')
          + (openSubs.length > 3 ? `, +${openSubs.length - 3} more` : '');
        lines.push(`${openSubs.length} subtask${openSubs.length === 1 ? '' : 's'} still open: ${list}.`);
      }
      if (openBlockers.length > 0) {
        const list = openBlockers.slice(0, 3).map(l => l.target.task_key).join(', ')
          + (openBlockers.length > 3 ? `, +${openBlockers.length - 3} more` : '');
        lines.push(`Blocked by: ${list}.`);
      }
      return await confirm({
        title: 'Open work detected',
        description: `${lines.join(' ')} Mark this task as Done anyway?`,
        confirmText: 'Done anyway',
        cancelText: 'Keep open',
        variant: 'destructive',
      });
    } catch (err) {
      console.warn('[done-guard]', err);
      return true;
    }
  };

  const updateField = async (field, value) => {
    if (!(await confirmBlockersIfMovingToDone(field, value))) return;
    try { await axios.put(`${API}/tasks/${task.task_id}`, { [field]: value }, h()); onUpdated(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };
  const updateFields = async (patch) => {
    if ('status' in patch && !(await confirmBlockersIfMovingToDone('status', patch.status))) return;
    try { await axios.put(`${API}/tasks/${task.task_id}`, patch, h()); onUpdated(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };
  const updateCustomField = async (fieldId, value) => {
    const cf = { ...(task?.custom_fields || {}), [fieldId]: value };
    try { await axios.put(`${API}/tasks/${task.task_id}`, { custom_fields: cf }, h()); onUpdated(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };
  const handlePostComment = async () => {
    if (!newComment.trim()) return; setPostingComment(true);
    try { await axios.post(`${API}/tasks/${task.task_id}/comments`, { content: newComment.trim() }, h()); setNewComment(''); fetchComments(); fetchActivities(); } catch {} finally { setPostingComment(false); }
  };
  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      await axios.put(`${API}/comments/${commentId}`, { content: editCommentText.trim() }, h());
      setEditingCommentId(null);
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save comment');
    }
  };
  const handleDeleteComment = async (commentId) => {
    if (!(await confirm({ title: 'Delete comment?', description: 'This cannot be undone.', confirmText: 'Delete', variant: 'destructive' }))) return;
    try { await axios.delete(`${API}/comments/${commentId}`, h()); fetchComments(); fetchActivities(); } catch {}
  };
  const handleDelete = async () => {
    if (!(await confirm({ title: `Delete ${task?.task_key}?`, description: 'This task and all its comments, attachments, and activity will be permanently removed.', confirmText: 'Delete task', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/tasks/${task.task_id}`, h());
      // Close the sheet first so the user gets immediate visual feedback that
      // the deletion went through. Then refetch in the background — without
      // the close call the sheet sticks around showing the just-deleted task.
      onClose?.();
      onUpdated?.();
      toast.success(`${task.task_key} deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete task');
    }
  };

  const getMemberName = (uid) => resolveUser(uid)?.name || 'Unknown';
  const getMemberAvatar = (uid) => resolveUser(uid)?.avatar || null;
  const timeAgo = (ds) => { const d = Date.now() - new Date(ds).getTime(); if (d < 60000) return 'just now'; if (d < 3600000) return `${Math.floor(d / 60000)}m`; if (d < 86400000) return `${Math.floor(d / 3600000)}h`; return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

  const formatActivity = (act) => {
    const name = getMemberName(act.user_id);
    if (act.action === 'created') return <><strong>{name}</strong> created this</>;
    if (act.action === 'commented') return <><strong>{name}</strong> commented</>;
    if (act.action === 'updated' && act.details) {
      const fl = FIELD_LABELS_MAP[act.details.field] || act.details.field;
      const from = act.details.field === 'status' ? (STATUS_LABELS[act.details.from] || act.details.from) : act.details.from;
      const to = act.details.field === 'status' ? (STATUS_LABELS[act.details.to] || act.details.to) : act.details.to;
      return <><strong>{name}</strong> changed {fl}: {from || 'none'} <ArrowRight size={9} className="inline mx-0.5" /> {to}</>;
    }
    return <><strong>{name}</strong> {act.action}</>;
  };

  // Layout-driven rendering
  const useSprints = sprints.length > 0 || !!task?.sprint_id;
  const layout = getEffectiveLayout(fieldLayouts, task?.work_type || 'task', customFields, project, useSprints);
  const applicableCf = (customFields || []).filter(cf => !cf.work_types?.length || cf.work_types.includes(task?.work_type || 'task'));
  const builtinIds = ['description', 'type', 'status', 'priority', 'assignee', 'sprint', 'due_date', 'tags', 'reporter', 'created'];

  const renderField = (fieldId, layout = 'main') => {
    if (!task) return null;
    if (fieldId === 'subtasks') {
      if (task.work_type === 'task' || task.parent_task_id) return null;
      return (
        <SubtasksSection key="subtasks"
          parentTask={task} projectId={task.project_id} allTasks={allTasks} members={members}
          onCreated={() => onUpdated && onUpdated()} onTaskClick={onTaskClick} canEdit={canEdit} />
      );
    }
    if (fieldId === 'linked_issues') {
      return <LinkedIssuesSection key="linked_issues" task={task} projectId={task.project_id} allTasks={allTasks} canEdit={canEdit} />;
    }
    if (builtinIds.includes(fieldId)) {
      return <BuiltinFieldRenderer key={fieldId} fieldId={fieldId} task={task} members={members} canEdit={canEdit} updateField={updateField} onPaste={handlePaste} onFileUpload={handleFileUpload} sprints={sprints} uploadImage={uploadImage} layout={layout} customStatuses={project?.custom_statuses} />;
    }
    const cf = applicableCf.find(f => f.field_id === fieldId);
    if (cf) return <CustomFieldRenderer key={fieldId} field={cf} task={task} canEdit={canEdit} updateCustomField={updateCustomField} onPaste={handlePaste} onFileUpload={handleFileUpload} uploadImage={uploadImage} layout={layout} />;
    return null;
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-[95vw] max-w-[95vw] sm:max-w-[95vw] p-0 flex flex-col"
        data-testid="task-sheet"
      >
        {/* Slim top bar — mirrors TaskDetailPage header */}
        <div className="h-12 flex items-center gap-3 px-6 border-b border-zinc-200 flex-shrink-0">
          <WorkTypeIcon type={task?.work_type || 'task'} size={16} />
          {/* Task key is a Jira-style permalink: hover shows full URL,
              click copies /browse/<KEY> to clipboard. Replaces the inert
              monospace text that used to sit here. */}
          <CopyTaskLink taskKey={task?.task_key} />
          <Badge className="text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 font-semibold">{WT_LABELS[task?.work_type] || 'Task'}</Badge>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            data-testid="fullscreen-task-button"
            onClick={() => { onClose(); navigate(`/tasks/${task?.task_id}`); }}
            className="h-7 text-[11px] text-zinc-500 hover:text-zinc-700"
          >
            <ArrowsOut size={14} className="mr-1" /> Expand
          </Button>
          {task && <WatchButton taskId={task.task_id} compact />}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="delete-task-button"
              onClick={handleDelete}
              className="h-7 text-[11px] text-red-400 hover:text-red-600 mr-6"
            >
              <Trash size={13} className="mr-1" /> Delete
            </Button>
          )}
          {/* Hidden a11y title — required by Radix Dialog */}
          <SheetTitle className="sr-only">{task?.task_key} {task?.title}</SheetTitle>
          <SheetDescription className="sr-only">Task details</SheetDescription>
        </div>

        {/* 2-column body — left: main content (description, subtasks, attachments, links, comments/history)
            right: 300px sidebar with Details fields + time tracking. Mirrors TaskDetailPage layout. */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* LEFT — main column */}
          <div className="flex-1 min-w-0 overflow-y-auto px-10 py-8" data-testid="task-sheet-main">
            <div className="max-w-[960px] mx-auto">
              <InlineField
                value={task?.title}
                canEdit={canEdit}
                maxLength={255}
                displayValue={<h1 className="font-['Outfit'] text-[22px] font-bold text-zinc-900 tracking-tight">{task?.title}</h1>}
                onSave={(v) => updateField('title', v)}
              />

              {/* Main zone fields (description, sub-tasks, linked items + custom main fields) */}
              <div className="mt-6 space-y-6">
                {layout.main.map(fId => renderField(fId))}
              </div>

              {/* Attachments */}
              {task && (
                <div className="mt-6">
                  <AttachmentsSection
                    taskId={task.task_id}
                    canEdit={canEdit}
                    currentUserId={user?.user_id}
                  />
                </div>
              )}

              <Separator className="my-8" />

              {/* Comments + History tabs at bottom of left column — same as TaskDetailPage */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="h-9 bg-transparent border-b border-zinc-100 rounded-none justify-start gap-0 p-0 w-full">
                  <TabsTrigger
                    value="comments"
                    className="text-[12px] font-semibold rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-3 py-2"
                    data-testid="comments-tab"
                  >
                    <ChatCircle size={13} className="mr-1" /> Comments {comments.length > 0 && `(${comments.length})`}
                  </TabsTrigger>
                  <TabsTrigger
                    value="activity"
                    className="text-[12px] font-semibold rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none px-3 py-2"
                    data-testid="activity-tab"
                  >
                    <ClockCounterClockwise size={13} className="mr-1" /> History
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="comments" className="mt-4 space-y-4">
                  {canEdit && (
                    <div className="mb-4">
                      <RichEditor
                        value={newComment}
                        onChange={setNewComment}
                        uploadImage={uploadImage}
                        mentionItems={projectMembers}
                        placeholder="Write a comment... Type @ to mention a teammate."
                        minHeight={80}
                      />
                      <div className="flex items-center justify-end mt-2">
                        <Button
                          onClick={handlePostComment}
                          disabled={postingComment || !newComment.trim()}
                          size="sm"
                          className="h-8 px-4 bg-blue-600 hover:bg-blue-700 text-white text-[11px]"
                          data-testid="post-comment-button"
                        >
                          <PaperPlaneRight size={14} className="mr-1" /> Post Comment
                        </Button>
                      </div>
                    </div>
                  )}
                  {comments.length === 0 && <p className="text-[13px] text-zinc-400 text-center py-6">No comments yet.</p>}
                  {comments.map((c) => {
                    const cu = resolveUser(c.user_id);
                    const jiraAuthor = c.imported_from === 'jira' && c.imported_author_name ? c.imported_author_name : null;
                    const isOwn = c.user_id === user?.user_id || canEdit;
                    const isEditing = editingCommentId === c.comment_id;
                    return (
                      <div key={c.comment_id} className="flex gap-3 group" data-testid={`comment-${c.comment_id}`}>
                        <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                          <AvatarImage src={cu?.avatar} />
                          <AvatarFallback className={`${getAvatarStyle(cu?.user_id || cu?.name).bg} text-white text-[9px] font-bold`}>{getInitials(cu?.name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[13px] font-bold text-zinc-800">{cu?.name || 'Unknown'}</span>
                            <span className="text-[10px] text-zinc-400">{timeAgo(c.created_at)}</span>
                            {c.updated_at && c.updated_at !== c.created_at && <span className="text-[9px] text-zinc-400 italic">(edited)</span>}
                            {isOwn && !isEditing && (
                              <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditingCommentId(c.comment_id); setEditCommentText(c.content); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-blue-600 transition-colors" title="Edit" data-testid={`edit-comment-${c.comment_id}`}>
                                  <PencilSimple size={13} />
                                </button>
                                <button onClick={() => handleDeleteComment(c.comment_id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-red-500 transition-colors" title="Delete" data-testid={`delete-comment-${c.comment_id}`}>
                                  <Trash size={13} />
                                </button>
                              </div>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="space-y-2">
                              <RichEditor value={editCommentText} onChange={setEditCommentText}
                                uploadImage={uploadImage} minHeight={80}
                                mentionItems={projectMembers}
                                placeholder="Edit your comment..." autoFocus />
                              <div className="flex items-center justify-end gap-1.5">
                                <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)} className="h-6 px-2 text-[10px] text-zinc-500" data-testid={`cancel-edit-comment-${c.comment_id}`}><XIcon size={10} className="mr-0.5" /> Cancel</Button>
                                <Button size="sm" onClick={() => handleEditComment(c.comment_id)} disabled={!editCommentText.trim()} className="h-6 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[10px]" data-testid={`save-edit-comment-${c.comment_id}`}><FloppyDisk size={10} className="mr-0.5" /> Save</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-[13px] text-zinc-700 leading-relaxed bg-zinc-50 rounded-lg px-3 py-2 border border-zinc-100 min-w-0 [overflow-wrap:anywhere]">
                                {jiraAuthor && <p className="text-[11px] text-zinc-500 mb-1.5" data-testid={`comment-jira-author-${c.comment_id}`}>Imported from Jira · <strong className="text-zinc-700">{jiraAuthor}</strong></p>}
                                {isHtml(c.content)
                                  ? <RichEditorContent html={c.content} />
                                  : <MentionContent content={c.content} currentUserId={user?.user_id} />}
                              </div>
                              {!isHtml(c.content) && <CommentAttachments content={c.content} />}
                              <CommentReactions comment={c} userId={user?.user_id} headers={getOrgHeaders()}
                                onUpdated={(updated) => setComments(prev => prev.map(cm => cm.comment_id === updated.comment_id ? updated : cm))} />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>

                <TabsContent value="activity" className="mt-4 divide-y divide-zinc-100" data-testid="history-list">
                  {activities.length === 0 && <p className="text-[13px] text-zinc-400 text-center py-6">No history yet. Field changes will appear here as they happen.</p>}
                  {activities.map((act) => (
                    <HistoryEntry key={act.activity_id} activity={act} members={members}
                      getMemberAvatar={getMemberAvatar} getMemberName={getMemberName} />
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* RIGHT — 320px sidebar with Details fields. Each row is a 2-col
              layout (label-left, value-right) for fast scanning + alignment.
              Slightly wider than the previous 300px to give long sprint /
              assignee names a bit more breathing room. */}
          <div className="w-[320px] border-l border-zinc-200 overflow-y-auto bg-zinc-50/60 flex-shrink-0" data-testid="task-sheet-sidebar">
            <div className="px-5 py-5 space-y-0.5">
              <h3 className="text-[10.5px] font-bold text-zinc-500 uppercase tracking-[0.14em] mb-3">Details</h3>
              <div className="rounded-lg bg-white ring-1 ring-zinc-200/70 p-2 divide-y divide-zinc-100">
                {layout.sidebar.map(fId => renderField(fId, 'sidebar'))}
              </div>
              <div className="pt-5">
                <TimeBlock task={task} project={project} canEdit={canEdit} onSave={updateFields} />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
